import React from 'react'
import { Create, useForm, useSelect } from '@refinedev/antd'
import { useSearchParams } from 'react-router'
import { Form, Input, Select, DatePicker, TimePicker, InputNumber } from 'antd'
import type { Tables } from '@muaa/shared'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { ImageUploadField } from '../../components/ImageUploadField'

// event_sessions stores its temporal fields as human-format TEXT (date='Sep 4',
// start_time='5:30 PM'), and the mobile app displays them VERBATIM. customParseFormat
// lets dayjs read those exact strings so the pickers round-trip them without ever
// reformatting what the app shows.
dayjs.extend(customParseFormat)

type EventSession = Tables<'event_sessions'>
type Event = Tables<'events'>

const DAY_OPTIONS = [
  { label: 'Friday', value: 'FRIDAY' },
  { label: 'Sabbath', value: 'SABBATH' },
  { label: 'Sunday', value: 'SUNDAY' },
]

// Format-preserving adapters: getValueProps parses the stored string → dayjs for the
// picker; normalize writes the dayjs back to the EXACT stored string format.
export const TIME_FMT = 'h:mm A' // e.g. "5:30 PM"
export const DATE_FMT = 'MMM D'  // e.g. "Sep 4" (a short label; the real date is on events.start_date)
export const timeValueProps = (v?: string | null) => ({ value: v ? dayjs(v, TIME_FMT) : undefined })
export const normalizeTime = (v: unknown) => (v ? (dayjs.isDayjs(v) ? v.format(TIME_FMT) : v) : null)
export const dateLabelValueProps = (v?: string | null) => ({ value: v ? dayjs(v, DATE_FMT) : undefined })
export const normalizeDateLabel = (v: unknown) => (v ? (dayjs.isDayjs(v) ? v.format(DATE_FMT) : v) : null)

export function EventSessionCreate() {
  const { formProps, saveButtonProps, form } = useForm<EventSession>()

  // "Add sub-event" from an event's show page arrives as
  // /event_sessions/create?event_id=<parent> — preset the (required) parent
  // select so the sub-event lands under the right event without re-picking it.
  const [searchParams] = useSearchParams()
  const presetEventId = searchParams.get('event_id') ?? undefined

  const { selectProps: eventSelectProps } = useSelect<Event>({
    resource: 'events',
    optionLabel: 'title',
    optionValue: 'id',
    pagination: { mode: 'off' },
  })

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Form.Item
          label="Event"
          name="event_id"
          initialValue={presetEventId}
          rules={[{ required: true, message: 'Please select an event' }]}
        >
          <Select {...eventSelectProps} placeholder="Select event" />
        </Form.Item>
        <Form.Item label="Title" name="title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Day" name="day">
          <Select options={DAY_OPTIONS} placeholder="Select day" />
        </Form.Item>
        <Form.Item
          label="Date"
          name="date"
          getValueProps={dateLabelValueProps}
          normalize={normalizeDateLabel}
          tooltip="A short day label shown on the session (e.g. 'Sep 4'). The full calendar date lives on the parent Event."
        >
          <DatePicker style={{ width: '100%' }} format={DATE_FMT} />
        </Form.Item>
        <Form.Item
          label="Start Time"
          name="start_time"
          getValueProps={timeValueProps}
          normalize={normalizeTime}
        >
          <TimePicker style={{ width: '100%' }} use12Hours format={TIME_FMT} minuteStep={5} />
        </Form.Item>
        <Form.Item
          label="End Time"
          name="end_time"
          getValueProps={timeValueProps}
          normalize={normalizeTime}
        >
          <TimePicker style={{ width: '100%' }} use12Hours format={TIME_FMT} minuteStep={5} />
        </Form.Item>
        <Form.Item label="Location" name="location">
          <Input />
        </Form.Item>
        <Form.Item label="Address" name="address">
          <Input />
        </Form.Item>
        <Form.Item label="Attire" name="attire">
          <Input />
        </Form.Item>
        <Form.Item label="Going Count" name="going_count" initialValue={0}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input.TextArea rows={4} />
        </Form.Item>
        <ImageUploadField
          form={form}
          name="image_url"
          label="Hero Image URL"
          placeholder="https://…/hero.jpg"
          folder="event_sessions"
          tooltip="Leave blank to fall back to the placeholder label below"
        />
        <Form.Item label="Image Label" name="image_label">
          <Input />
        </Form.Item>
        <Form.Item label="Sort Order" name="sort_order">
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Create>
  )
}
